import { PaymentCollectionType } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/payment_collection_type_pb'
import { UserProfile } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/user_profile_pb'
import { GetStoreConfigPublicResponse } from '@fingertip/creator-proto/gen/fingertip/creator/store_config/v1/store_config_pb'
import { GetStoreCouponPublicResponse } from '@fingertip/creator-proto/gen/fingertip/creator/store_coupon/v1/store_coupon_pb'
import { GetStripeBookingPaymentAmountPublicResponse } from '@fingertip/creator-proto/gen/fingertip/creator/store_payment/v1/store_payment_pb'
import React, { useCallback, useEffect, useMemo } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { CustomerBookerSchema } from '@/components/booking/booker/booker'
import { BookerPaymentSummary } from '@/components/booking/booker/components/booker-payment-summary'
import { CheckoutWrapper } from '@/components/checkout/checkout-wrapper'
import { StripeSetupWrapper } from '@/components/checkout/stripe-setup-wrapper'
import { PaymentDetailsFormSchema } from '@/components/checkout/types'
import { Button } from '@/components/ui/button'
import { useBookingCheckout } from '@/lib/hooks/use-booking-checkout'
import { BookingSelection } from '@/lib/hooks/use-booking-selection'
import { useBookingSetup } from '@/lib/hooks/use-booking-setup'

type Props = {
  form: any
  siteSlug: string
  storeConfigData: GetStoreConfigPublicResponse
  bookingSelection: BookingSelection
  stripeConnectedAccountId?: string
  currentUser?: UserProfile
  submitHandler: (schema: CustomerBookerSchema) => void
  appliedCoupon?: string
  setAppliedCoupon: (coupon: string) => void
  bookingPaymentData?: GetStripeBookingPaymentAmountPublicResponse
  storeCouponData?: GetStoreCouponPublicResponse
}

export const BookerStagePayment = ({
  form,
  siteSlug,
  storeConfigData,
  bookingSelection,
  stripeConnectedAccountId,
  currentUser,
  submitHandler,
  appliedCoupon,
  setAppliedCoupon,
  bookingPaymentData,
  storeCouponData,
}: Props) => {
  const { t } = useTranslation()
  const { onPaymentConfirmed, getPaymentParams, applyCouponOnPayment } =
    useBookingCheckout({
      siteSlug,
    })

  const { onSetupConfirmed, getSetupParams, applyCouponOnSetup } =
    useBookingSetup({
      siteSlug,
    })

  const watchCouponCode = form.watch('couponCode')

  const checkoutOptions = useMemo(() => {
    return {
      mode: 'payment' as const,
      amount: bookingPaymentData?.depositAmountInCents,
      currency: storeConfigData?.currency || 'usd',
    }
  }, [storeConfigData?.currency, bookingPaymentData?.depositAmountInCents])

  useEffect(() => {
    if (
      // Coupon code was added
      ((storeCouponData && storeCouponData.coupon && watchCouponCode) ||
        // Coupon code was removed
        watchCouponCode === undefined) &&
      watchCouponCode !== appliedCoupon
    ) {
      if (
        !!checkoutOptions.amount &&
        storeConfigData?.config?.paymentCollectionType ===
          PaymentCollectionType.COLLECT_UPFRONT
      ) {
        setAppliedCoupon(watchCouponCode)
        applyCouponOnPayment(
          bookingSelection.bookingItemsAttendanceType,
          watchCouponCode,
          checkoutOptions.amount,
        )
      } else if (
        storeConfigData?.config?.paymentCollectionType ===
        PaymentCollectionType.CAPTURE_DETAILS
      ) {
        if (bookingSelection.eventTypes) setAppliedCoupon(watchCouponCode)
        applyCouponOnSetup(
          bookingSelection.bookingItemsAttendanceType,
          watchCouponCode,
        )
      } else {
        setAppliedCoupon(watchCouponCode)
      }
    }
  }, [
    appliedCoupon,
    applyCouponOnPayment,
    applyCouponOnSetup,
    bookingSelection.bookingItemsAttendanceType,
    bookingSelection.eventTypes,
    checkoutOptions.amount,
    setAppliedCoupon,
    storeConfigData?.config?.paymentCollectionType,
    storeCouponData,
    watchCouponCode,
  ])

  const watchEmail = form.watch('email')
  const defaultValues = useMemo(
    () => ({
      contact: {
        email: watchEmail,
        emailOptin: true,
      },
      billing: {
        name: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
      },
      shipping: {
        name: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
      },
      shippingRateId: '',
    }),
    [watchEmail],
  )

  const handleSetupComplete = useCallback(
    async ({ input }: { input: PaymentDetailsFormSchema }) => {
      await onSetupConfirmed({ input })
      await form.handleSubmit(submitHandler)()
    },
    [form, onSetupConfirmed, submitHandler],
  )

  const handlePaymentComplete = useCallback(
    async ({ input }: { input: PaymentDetailsFormSchema }) => {
      await onPaymentConfirmed({ input })
      await form.handleSubmit(submitHandler)()
    },
    [form, onPaymentConfirmed, submitHandler],
  )

  return (
    <>
      <BookerPaymentSummary
        form={form}
        siteSlug={siteSlug}
        storeConfig={storeConfigData?.config}
        bookingSelection={bookingSelection}
        bookingPayment={bookingPaymentData}
        bookingCoupon={storeCouponData}
      />

      {/* Total to pay > 0, UPFRONT */}
      {(bookingPaymentData?.totalAmountInCents || 0) > 0 &&
        storeConfigData?.config?.paymentCollectionType ===
          PaymentCollectionType.COLLECT_UPFRONT &&
        stripeConnectedAccountId && (
          <CheckoutWrapper
            storeConfigData={storeConfigData}
            options={checkoutOptions}
            siteSlug={siteSlug}
            defaultValues={defaultValues}
            getPaymentParams={getPaymentParams}
            onPaymentConfirmed={handlePaymentComplete}
            currentUser={currentUser}
          />
        )}
      {/* Total to pay > 0, CAPTURE */}
      {(bookingPaymentData?.totalAmountInCents || 0) > 0 &&
        storeConfigData?.config?.paymentCollectionType ===
          PaymentCollectionType.CAPTURE_DETAILS &&
        stripeConnectedAccountId && (
          <StripeSetupWrapper
            storeConfigData={storeConfigData}
            siteSlug={siteSlug}
            options={checkoutOptions}
            defaultValues={defaultValues}
            getSetupParams={getSetupParams}
            onSetupConfirmed={handleSetupComplete}
          />
        )}

      {/* Total to pay is now 0, because of COUPON applied */}
      {bookingPaymentData?.totalAmountInCents === 0 && (
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit(submitHandler)}
            className="flex size-full flex-col"
          >
            <Button type="submit" className="w-full">
              {t('common:schedule_event')}
            </Button>{' '}
          </form>
        </FormProvider>
      )}
    </>
  )
}
